import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { authClient } from '../../lib/auth-client'
import { 
  Key, 
  Activity, 
  Zap, 
  ArrowRight,
  Terminal,
  Copy,
  CheckCircle2
} from 'lucide-react'

interface ApiKey {
  id: string
  name: string
  prefix: string
  createdAt: string
  lastUsedAt: string | null
}

interface UsageStats {
  totalRequests: number
  requestsToday: number
  apiCalls: number
}

export function DashboardOverview() {
  const { data: session } = authClient.useSession()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      // Fetch API keys
      const keysRes = await fetch('/api/v1/keys', {
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })
      if (keysRes.ok) {
        const keysData = await keysRes.json()
        setKeys(keysData.keys || [])
      }

      // Fetch usage stats
      const usageRes = await fetch('/api/v1/usage/summary', {
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })
      if (usageRes.ok) {
        const usageData = await usageRes.json()
        setUsage(usageData)
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedKey(id)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | Twenty</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">
            Welcome back{session?.user?.name ? `, ${session.user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-muted-foreground">
            Manage your API keys and monitor usage
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="/dashboard/keys"
            className="group p-5 border border-border rounded-sm hover:border-primary/50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <Key className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium mb-1 flex items-center gap-2">
                  Create API Key
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  Generate keys to access the infrastructure API
                </p>
              </div>
            </div>
          </a>

          <a
            href="https://docs.twenty.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-5 border border-border rounded-sm hover:border-primary/50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <Terminal className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium mb-1 flex items-center gap-2">
                  Read Documentation
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  Learn how to use the API effectively
                </p>
              </div>
            </div>
          </a>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-factory p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? '-' : keys.length}
                </p>
                <p className="text-sm text-muted-foreground">Active API Keys</p>
              </div>
            </div>
          </div>

          <div className="card-factory p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? '-' : usage?.requestsToday?.toLocaleString() || '0'}
                </p>
                <p className="text-sm text-muted-foreground">Requests Today</p>
              </div>
            </div>
          </div>

          <div className="card-factory p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-primary">$99/mo</p>
                <p className="text-sm text-muted-foreground">Current Plan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Keys */}
        <div className="card-factory">
          <div className="p-5 border-b border-border">
            <h2 className="font-medium">Your API Keys</h2>
          </div>
          
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse text-muted-foreground" aria-live="polite">Loading keys…</div>
            </div>
          ) : keys.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center mx-auto mb-4">
                <Key className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No API keys yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first key to start using the API
              </p>
              <a href="/dashboard/keys" className="btn-factory">
                Create key
              </a>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {keys.slice(0, 3).map((key) => (
                <div key={key.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Key className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{key.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {key.prefix}••••••••
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(`${key.prefix}...`, key.id)}
                    className="p-2 hover:bg-secondary rounded-sm transition-colors"
                    title="Copy key ID"
                    aria-label="Copy key ID"
                  >
                    {copiedKey === key.id ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {keys.length > 3 && (
            <div className="p-4 border-t border-border">
              <a 
                href="/dashboard/keys" 
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View all {keys.length} keys
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
